import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useSurveyStore } from 'Store/Survey'
import { useAuthStore } from 'Store/Auth'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'

import { INITIAL_PRODUCTION_ORDER_REQUEST, createEmptyProductionOrderItem } from './initialState'
import { apiToForm, formToApi } from './mapper'
import type {
  CustomerGrade,
  ProductionFactoriesState,
  ProductionOrderItem,
  ProductionOrderRequestFormState,
  ResponsibilityOwner,
} from './types'

import useFetchSurveyDetail from '../../useFetchSurveyDetail'

type FactoryKey = keyof ProductionFactoriesState

const useViewModel = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const isView = location.search.includes('view')
  const { isSurveyLoaded } = useFetchSurveyDetail()
  const roleId = useAuthStore(state => state.profile?.roleId ?? 0, shallow)

  const { getProductionOrderRequest, putProductionOrderRequest, downloadProductionOrderApprovalPdf } =
    useSurveyStore(
    state => ({
      getProductionOrderRequest: state.getProductionOrderRequest,
      putProductionOrderRequest: state.putProductionOrderRequest,
      downloadProductionOrderApprovalPdf: state.downloadProductionOrderApprovalPdf,
    }),
    shallow
  )

  const [form, setForm] = useState<ProductionOrderRequestFormState>(
    INITIAL_PRODUCTION_ORDER_REQUEST
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const fetchData = useCallback(async () => {
    if (!id || !isSurveyLoaded) return
    setLoading(true)
    const { success, data } = await getProductionOrderRequest(parseInt(id))
    if (!success) {
      toast.error(data)
      setLoading(false)
      return
    }
    setForm(apiToForm(data))
    setLoading(false)
  }, [getProductionOrderRequest, id, isSurveyLoaded])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const setField = <K extends keyof ProductionOrderRequestFormState>(
    key: K,
    value: ProductionOrderRequestFormState[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const setFactory = (key: FactoryKey, patch: Partial<ProductionFactoriesState[FactoryKey]>) => {
    setForm(prev => ({
      ...prev,
      productionFactories: {
        ...prev.productionFactories,
        [key]: { ...prev.productionFactories[key], ...patch },
      },
    }))
  }

  const setDocument = (key: keyof ProductionOrderRequestFormState['documents'], value: boolean | string) => {
    setForm(prev => ({
      ...prev,
      documents: { ...prev.documents, [key]: value },
    }))
  }

  const setResponsibilityOwner = (rowIndex: number, owner: ResponsibilityOwner) => {
    setForm(prev => ({
      ...prev,
      responsibility: prev.responsibility.map(row =>
        row.rowIndex === rowIndex ? { ...row, owner } : row
      ),
    }))
  }

  const addItemRow = () => {
    setForm(prev => ({ ...prev, items: [...prev.items, createEmptyProductionOrderItem()] }))
  }

  const removeItemRow = (rowId: string) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.length <= 1 ? prev.items : prev.items.filter(row => row.id !== rowId),
    }))
  }

  const updateItemRow = (rowId: string, field: keyof Omit<ProductionOrderItem, 'id'>, value: string) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map(row => (row.id === rowId ? { ...row, [field]: value } : row)),
    }))
  }

  const save = async () => {
    if (!id || isView) return
    setSaving(true)
    const { success, data } = await putProductionOrderRequest(parseInt(id), formToApi(form))
    setSaving(false)
    if (!success) {
      toast.error(data)
      return
    }
    Swal.fire({
      title: 'สำเร็จ',
      text: 'บันทึกใบขออนุมัติออกใบสั่งผลิตสำเร็จ',
      icon: 'success',
      confirmButtonText: 'ตกลง',
    })
    setForm(apiToForm(data))
  }

  const onDownloadPdf = async () => {
    if (!id) return
    setDownloading(true)
    toast.info('กำลังดาวน์โหลดไฟล์ PDF กรุณารอสักครู่...')
    const { success, data } = await downloadProductionOrderApprovalPdf(parseInt(id))
    setDownloading(false)
    if (!success) {
      toast.error(data)
      return
    }
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `production_order_approval_${id}.pdf`)
    document.body.appendChild(link)
    link.click()
  }

  return {
    roleId,
    isView,
    form,
    loading,
    saving,
    downloading,
    setField,
    setFactory,
    setDocument,
    setResponsibilityOwner,
    setCustomerGrade: (grade: CustomerGrade) => setField('customerGrade', grade),
    addItemRow,
    removeItemRow,
    updateItemRow,
    save,
    onDownloadPdf,
  }
}

export default useViewModel
